import React, { Component, PropTypes } from 'react';
import classnames from 'classnames';
import partial from 'lodash/partial';
import Carousel from 'react-slick';
import Tooltip from 'react-tooltip';
import ReputationChart from 'common/components/reputation_chart';
import { LoadingIndicator } from 'common/components/preloader';
import Avatar from 'common/components/avatar';
import Attribute from 'marketing/common/attribute';
import { RightNavButton, LeftNavButton } from 'common/components/carousel_controls';
import { MOBILE_BREAKPOINT_PX, DESKTOP_BREAKPOINT_PX } from 'common/variables';
import { isMobile } from 'common/utils';

/**
 * Suggested creatives list for 1-1 projects.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class SuggestedCreatives extends Component {
  constructor(props) {
    super(props)

    this.selectCreative = this.selectCreative.bind(this)
  }

  componentDidMount() {
    const { fetchSuggestedCreatives, subCategoryId, excludeIds } = this.props;
    fetchSuggestedCreatives(subCategoryId, excludeIds);
  }

  selectCreative(e, creative) {
    e.preventDefault()
    e.stopPropagation()
    this.props.selectCreative(creative)
  }

  openProfile(creative) {
    window.open(`/users/${creative.username}/portfolio/`, '_blank')
  }

  renderContent() {
    const { isFetching, creatives, selectedCreative } = this.props;
    const carouselSettings = {
      slidesToShow: 4,
      slidesToScroll: 4,
      infinite: false,
      dots: false,
      arrows: true,
      prevArrow: <LeftNavButton />,
      nextArrow: <RightNavButton />,
      responsive: [
        {
          breakpoint: MOBILE_BREAKPOINT_PX,
          settings: { slidesToShow: 1, slidesToScroll: 1, arrows: true }
        },
        {
          breakpoint: DESKTOP_BREAKPOINT_PX,
          settings: { slidesToShow: 2, slidesToScroll: 2 }
        },
      ]
    };

    /* fetching creatives list */
    if (isFetching) {
      return <LoadingIndicator/>
    }

    /* no creative found */
    if (creatives.length == 0) {
      return(
        <div className="empty-state no-results">
          <div className="empty-state-content">
            <h3>No creatives found.</h3>
          </div>
        </div>
      );
    }

    /* creatives list carousel */
    return (
      <Carousel {...carouselSettings}>
        {creatives.map((c, i) => {

          const reputationLabel = c.is_elite? 'Elite': (c.is_platinum? 'Platinum' : '');
          const selected = selectedCreative && selectedCreative.id === c.id;
          const notSelected = selectedCreative && selectedCreative.id !== c.id;
          const tileClasses = classnames("tile creative-tile", {
            "selected": selected,
            "not-selected": notSelected
          });

          return (
            <div key={i} className="columns">
              <div className={tileClasses}>
                <div className="creative-header">
                  <div className="creative-meta">
                    <h4 className="username" onClick={partial(this.openProfile, c)}>{c.username}</h4>
                    <div className="location">
                      { c.country_full_name? c.country_full_name : <span>&nbsp;</span> }
                    </div>
                  </div>
                  <div className="row">
                    <div className="columns small-4">
                      <Avatar user={c} extraClasses="avatar-md avatar-border"
                        onClick={partial(this.openProfile, c)}/>
                    </div>

                    <div className="columns small-4">
                      <ReputationChart reputation={parseInt(c.reputation)}
                        containerClass="reputation" withTooltip={true}/>
                    </div>

                    {reputationLabel &&
                    <div className="columns small-8">
                      <div className={classnames("tag tag-sm tag-highlight", {
                        "platinum": c.is_platinum,
                        "elite": c.is_elite
                      })} data-tip={true} data-for={`rep-tag-tooltip-${i}`}
                        data-event={isMobile()? "click" : undefined}>
                        {reputationLabel}
                      </div>
                      <Tooltip class="tag-tooltip tooltip" effect="solid" place="right"
                          id={`rep-tag-tooltip-${i}`}>
                        Platinum creatives have a reputation of 80-90,<br/>
                        while Elite creatives have a reputation of 90 to 100.<br/>
                        These are the best, most experienced creatives on<br/>
                        crowdspring, as measured by the quality of their work.
                      </Tooltip>
                    </div>}
                  </div>
                </div>
                <div className="row columns">
                  { c.portfolio_thumbnail_1 ?
                  <img src={c.portfolio_thumbnail_1} className="portfolio-item"
                    onClick={partial(this.openProfile, c)}/>:
                  <div className="empty-portfolio">
                    <div className="empty-portfolio-content">
                      <h4>{c.username} has been a bit slow creating their portfolio.</h4>
                    </div>
                    <img src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_explore_no-portfolio_lagrell-2x.png"
                        alt="Indifferent Turtle by lagrell"
                        className="empty-portfolio-image"
                      />
                      <Attribute to="lagrell"/>
                  </div> }
                </div>
                <div className="btn-container">
                  <button
                    className="btn btn-primary btn-block"
                    onClick={(e) => this.selectCreative(e, selected? null : c)}
                  >
                    {selected ? <span>Selected <i className="fa fa-check-circle"/></span> : "Select"}
                  </button>
                </div>
              </div>
            </div>)
        })}
      </Carousel>)
  }

  render() {
    const { isFetching, creatives } = this.props;

    return (
      <section className="suggested-creatives">
        <div className="row columns">
          <div className="brief-title">
            <h2>Select a Creative</h2>
            { !isFetching && creatives.length > 0 &&
            <p>Here are { creatives.length } terrific creatives who match your needs:</p> }
          </div>
        </div>
        <div className="row columns">
          { this.renderContent() }
        </div>
      </section>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {array} creatives - suggested creatives list
 * @property {bool} isFetching - whether creatives are being fetched
 * @property {object} selectedCreative - selected creative
 * @property {func} selectCreative - select a given creative
 * @property {func} fetchSuggestedCreatives - fetch suggested creatives list
 * @property {number} subCategoryId - sub-category ID for which creative suggestions should be
 *                                    fetched
 * @property {array} excludeIds - IDs of creatives to be excluded from suggestions
 */
SuggestedCreatives.propTypes = {
  creatives: PropTypes.array,
  isFetching: PropTypes.bool,
  selectedCreative: PropTypes.object,
  selectCreative: PropTypes.func,
  fetchSuggestedCreatives: PropTypes.func,
  subCategoryId: PropTypes.number,
  excludeIds: PropTypes.array,
};



// WEBPACK FOOTER //
// ./project/components/brief/suggested_creatives_1_1.jsx